/* eslint-disable no-param-reassign, prefer-const, no-restricted-properties */

import ButtonBase from '@material-ui/core/ButtonBase'
import Collapse from '@material-ui/core/Collapse'
import Fade from '@material-ui/core/Fade'
import { withStyles } from '@material-ui/core/styles'
import cn from 'classnames'
import { isNil } from 'lodash'
import { Component, createRef } from 'react'
import Dropzone from 'react-dropzone'

import Icon from '../Icon'
import LabelWrapper from '../LabelWrapper'
import File from './File'
import Progress from './Progress'

const styles = theme => ({
  labelWrap: {
    '& .label-inline $wrapper.has-label': {
      margin: 0,
    },
    '& .has-error': {
      '& $wrapper': {
        borderColor: theme.status.errorBorder,
        backgroundColor: theme.status.errorBG,
      },
    },
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    padding: '24px',
    backgroundColor: `var(--gameday-inputField-inputBGColor, ${theme.inputField.inputBGColor})`,
    border: theme.borders.dashed,
    borderRadius: theme.borders.borderRadius,
    fontSize: theme.typography.fontSizeSm,
    color: `var(--gameday-inputField-inputLabelColor, ${theme.inputField.inputLabelColor})`,
    '&.accept': {
      borderColor: theme.palette.primary.main,
      '& $uploadIcon': {
        fill: theme.palette.primary.main,
      },
    },
    '&.reject': {
      borderColor: theme.status.errorBorder,
    },
    '&.has-label': {
      marginTop: '8px',
    },
    '&.disabled': {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      padding: '8px',
      backgroundColor: '#e8e8e8',
    },
  },
  dropText: {
    textAlign: 'center',
    '& button': {
      '-moz-appearance': 'none',
      '-webkit-appearance': 'none',
    },
  },
  buttonAlign: {
    display: 'inline-flex',
  },
  choose: {
    fontWeight: theme.typography.fontWeightBolder,
    color: theme.palette.primary.main,
    fontSize: theme.typography.fontSizeSm,
    margin: '0 4px',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  uploadIcon: {
    fill: '#757575',
  },
  error: {
    color: theme.status.errorColor,
    fontSize: theme.typography.fontSizeSm,
    marginTop: '8px',
  },
  topMargin: {
    marginTop: '8px',
  },
  hideDrop: {
    display: 'none',
  },
})

const convertToMB = size => {
  const converted = size / 1024 ** 2
  return converted.toFixed(0)
}

const extensions = {
  audio: 'audio/*',
  css: 'text/css',
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  gif: 'image/gif',
  htm: 'text/html',
  html: 'text/html',
  image: 'image/*',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  json: 'application/json',
  markdown: 'text/markdown',
  md: 'text/markdown',
  mp3: 'audio/mp3',
  mp4: 'video/mp4',
  pdf: 'application/pdf',
  plain: 'text/plain',
  png: 'image/png',
  svg: 'image/svg+xml',
  txt: 'text/plain',
  video: 'video/*',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xml: 'text/xml',
  zip: 'application/zip',
}

const getFileTypes = types => {
  const acceptedTypes = Object.keys(extensions)
    .filter(key => types.includes(key))
    .reduce((obj, key) => {
      obj[key] = extensions[key]
      return Object.values(obj)
    }, {})
  return acceptedTypes.join(', ')
}

class FileUpload extends Component {
  constructor(props) {
    super(props)
    const { handleDrop, src } = this.props
    this.onDrop = (newFiles, rejectedFiles) => {
      this.setState({ hideFailed: false, isUploading: true })

      if (rejectedFiles.length > 0) {
        this.setState({ files: newFiles })
        setTimeout(() => {
          this.setState({
            hideFailed: true,
          })
        }, 2000)
      } else {
        const { files } = this.state
        this.setState({ files: [...newFiles, ...files], hideDropzone: true })
      }

      // Do not change this.props.isEdit as only this way it passes latest value
      handleDrop(newFiles, this.props.isEdit)
    }

    this.state = {
      files: [],
      hideDropzone: !!src,
      hideFailed: false,
      loaded: false,
      successfulUpload: false,
    }

    this.handleCancel = this.handleCancel.bind(this)
    this.handleDelete = this.handleDelete.bind(this)
  }

  static getDerivedStateFromProps(props, state) {
    if (!state.loaded && state.files.length < 1 && (props.files || []).length > 0) {
      return { files: props.files, hideDropzone: true, loaded: true, successfulUpload: true }
    }

    if (props.files && props.files.length === state.files.length && props.files.length !== 0 && state.isUploading) {
      return {
        ...state,
        isUploading: false,
      }
    }

    if (
      !isNil(props.isEdit) &&
      !props.isEdit &&
      props.files &&
      props.files.length === 0 &&
      !isNil(props.uploadProgress) &&
      !props.uploading &&
      !state.isUploading &&
      state.files.length > 0
    ) {
      return {
        ...state,
        files: [],
        isUploading: false,
      }
    }

    return { successfulUpload: state.successfulUpload || props.successfulUpload }
  }

  handleCancel(file) {
    const { files } = this.state
    const { onCancel } = this.props
    const updatedFiles = [...files]
    updatedFiles.splice(updatedFiles.indexOf(file), 1)
    this.setState({ files: updatedFiles, hideDropzone: false })
    onCancel(file)
  }

  handleDelete(file) {
    const { files } = this.state
    const { onDelete } = this.props
    const updatedFiles = [...files]
    updatedFiles.splice(updatedFiles.indexOf(file), 1)
    this.setState({ files: updatedFiles, hideDropzone: false })
    onDelete(file)
  }

  handleDeleteSrc() {
    const { handleDeleteSrc, multipleFiles } = this.props
    this.setState({ hideDropzone: false })

    if (!multipleFiles) {
      this.setState({ files: [] })
    }

    handleDeleteSrc()
  }

  render() {
    const {
      classes,
      className,
      customCol,
      customMargin,
      customPadding,
      customWidth,
      disabled,
      errorText,
      fileType,
      fullWidth,
      helperText,
      id,
      isLabelInline,
      isRequired,
      isToolTip,
      justify,
      label,
      maxSize,
      multipleFields,
      multipleFiles,
      noMobile,
      notWrapper,
      noWrap,
      onCancel,
      onDelete,
      previewFileType,
      setTooltipDangerously,
      spacing,
      src,
      toolTipIcon,
      toolTipIconColor,
      toolTipIconSize,
      toolTipPosition,
      toolTipText,
      uploading,
      uploadProgress,
      willDelete,
      withBorder,
      withDivider,
      withPreview,
      forceShowDropzone,
      forceHideDropzone,
    } = this.props

    const { files, hideDropzone, hideFailed, successfulUpload } = this.state

    const dropzoneRef = createRef()

    const openDialog = () => {
      if (dropzoneRef.current) {
        dropzoneRef.current.open()
      }
    }

    const acceptedTypes = getFileTypes(fileType)

    return (
      <LabelWrapper
        className={cn(className, classes.labelWrap)}
        customCol={customCol}
        customMargin={customMargin}
        customPadding={customPadding}
        customWidth={customWidth}
        disabled={disabled}
        errorText={errorText}
        fullWidth={fullWidth}
        helperText={helperText}
        id={id}
        isLabelInline={isLabelInline}
        isRequired={isRequired}
        isToolTip={isToolTip}
        justify={justify}
        label={label}
        multipleFields={multipleFields}
        noMobile={noMobile}
        notWrapper={notWrapper}
        noWrap={noWrap}
        spacing={spacing}
        setTooltipDangerously={setTooltipDangerously}
        toolTipIcon={toolTipIcon}
        toolTipIconColor={toolTipIconColor}
        toolTipIconSize={toolTipIconSize}
        toolTipPosition={toolTipPosition}
        toolTipText={toolTipText}
        willDelete={willDelete}
        withBorder={withBorder}
        withDivider={withDivider}
      >
        <Dropzone
          accept={acceptedTypes}
          disabled={disabled}
          onDrop={this.onDrop}
          noClick
          noKeyboard
          maxSize={maxSize}
          multiple={multipleFiles}
          ref={dropzoneRef}
        >
          {({
            getRootProps,
            getInputProps,
            isDragActive,
            // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
            isDragAccept,
            isDragReject,
            // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
            acceptedFiles,
            rejectedFiles,
          }) => (
            <section className="container">
              {forceHideDropzone ? (
                // biome-ignore lint/complexity/noUselessFragments: TODO
                <></>
              ) : (
                // biome-ignore lint/complexity/noUselessFragments: TODO
                <>
                  {!forceShowDropzone ? (
                    <Collapse in={!hideDropzone} out={hideDropzone} timeout={{ enter: 500, exit: 1000 }}>
                      <div
                        {...getRootProps({
                          className: cn(classes.wrapper, {
                            accept: isDragActive,
                            reject: isDragReject,
                            'has-label': label,
                            disabled,
                          }),
                        })}
                        style={customWidth && { width: customWidth }}
                      >
                        <input {...getInputProps()} />
                        <Icon
                          className={classes.uploadIcon}
                          iconName="upload"
                          iconSize={24}
                          alt="upload"
                          viewBox="-2 -2 24 24"
                        />
                        <div className={classes.dropText}>
                          Drop your document here or
                          <span className={classes.buttonAlign}>
                            <ButtonBase className={cn(classes.choose, 'choose')} onClick={openDialog}>
                              {multipleFiles ? 'choose file(s)' : 'choose a file'}
                            </ButtonBase>
                          </span>
                          to upload
                        </div>
                      </div>
                    </Collapse>
                  ) : (
                    <div
                      {...getRootProps({
                        className: cn(classes.wrapper, {
                          accept: isDragActive,
                          reject: isDragReject,
                          'has-label': label,
                          disabled,
                        }),
                      })}
                      style={customWidth && { width: customWidth }}
                    >
                      <input {...getInputProps()} />
                      <Icon
                        className={classes.uploadIcon}
                        iconName="upload"
                        iconSize={24}
                        alt="upload"
                        viewBox="-2 -2 24 24"
                      />
                      <div className={classes.dropText}>
                        Drop your document here or
                        <span className={classes.buttonAlign}>
                          <ButtonBase className={classes.choose} onClick={openDialog}>
                            {multipleFiles ? 'choose file(s)' : 'choose a file'}
                          </ButtonBase>
                        </span>
                        to upload
                      </div>
                    </div>
                  )}
                </>
              )}
              <aside style={customWidth && { width: customWidth }}>
                {files &&
                  !src &&
                  // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
                  files.map((file, index) => (
                    <Fade in={file.name && true} key={file.name} timeout={500}>
                      <div key={file.name}>
                        <File
                          disabled={disabled}
                          className={classes.topMargin}
                          file={file}
                          icon={!uploading && 'tick'}
                          src={src}
                          success={successfulUpload}
                          uploading={uploading}
                          variant="success"
                          onCancel={onCancel && (() => this.handleCancel(file, onCancel))}
                          onDelete={onDelete && (() => this.handleDelete(file, onDelete))}
                          withPreview={!multipleFiles && withPreview}
                        >
                          {!uploading ? (
                            <Progress variant="success" progress={100} />
                          ) : (
                            <Progress
                              variant="uploading"
                              progress={uploadProgress[file.name] ? uploadProgress[file.name].percentage : 0}
                            />
                          )}
                        </File>
                      </div>
                    </Fade>
                  ))}
                {src && (
                  <File
                    disabled={disabled}
                    className={classes.topMargin}
                    src={src}
                    success={successfulUpload}
                    uploading={uploading}
                    onCancel={onCancel && (() => this.handleCancel(file, onCancel))}
                    onDelete={onDelete && (() => this.handleDeleteSrc())}
                    withPreview={!multipleFiles && withPreview}
                    previewFileType={previewFileType}
                  />
                )}
                {rejectedFiles && (
                  // biome-ignore lint/complexity/noUselessFragments: TODO
                  <>
                    {rejectedFiles.length > 1 ? (
                      <div className={classes.error}>Too many files</div>
                    ) : (
                      rejectedFiles.map(file => (
                        <>
                          <Collapse in={!hideFailed} out={hideFailed} key={file.name} timeout={500}>
                            <div className={classes.topMargin} key={file.name}>
                              <File file={file} icon="Info" variant="error" disabled={disabled}>
                                <Progress variant="error" progress={100} />
                              </File>
                            </div>
                          </Collapse>
                          <div className={classes.error}>
                            {file.size > maxSize ? (
                              `The file you are trying to upload is larger than ${convertToMB(maxSize)}mb`
                            ) : (
                              <div>
                                The file you are trying to upload is an unsupported format. Only the following file
                                types are accepted: <strong>{fileType.join(', ')}</strong>
                              </div>
                            )}
                          </div>
                        </>
                      ))
                    )}
                  </>
                )}
              </aside>
            </section>
          )}
        </Dropzone>
      </LabelWrapper>
    )
  }
}

FileUpload.defaultProps = {
  fileType: ['jpg', 'png', 'pdf'],
  maxSize: 5242880,
  multipleFiles: false,
  onCancel: null,
  onDelete: null,
  uploading: false,
  uploadProgress: {},
  forceShowDropzone: false,
}

const FileUploadComponent = withStyles(styles)(FileUpload)
FileUploadComponent.displayName = 'FileUpload'
export default FileUploadComponent
